import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Archive, ArchiveRestore, CircleAlert, DoorOpen, KeyRound, LocateFixed, MapPin, Pencil, Users } from 'lucide-react';
import { adminQueries, getErrorMessage, hasProblemType, keys } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import {
  COORDINATES_SOURCE_LABELS,
  formatArea,
  formatDate,
  formatNumber,
  hasPermission,
  labelOf,
  OWNERSHIP_PARTY_ROLE_LABELS,
  TENANCY_PARTY_ROLE_LABELS,
} from '@blimo/shared';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  DataTable,
  DescriptionList,
  EmptyState,
  ErrorState,
  PageHeader,
  StatCard,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
  ToggleGroup,
} from '@blimo/ui';
import { DetailSkeleton, ExternalReferences, PartyLink, PartyList, UnitLabel } from '../../components/data.jsx';
import { PROPERTY_TABS, tabAllowed } from './tabs.js';
import { PropertyEditSheet } from './edit-sheet.jsx';
import { PropertyFinanceTab } from '../finance/property-tab.jsx';
import { PropertyRolesCard } from './roles.jsx';
import { DocumentManager } from '../../components/documents/document-manager.jsx';
import { HistoryPanel } from '../../components/history-panel.jsx';
import { PropertyCommunicationTab, PropertyDocumentChecksTab, PropertyMetersTab } from './related-tabs.jsx';
import { DEFAULT_PAGE_SIZE, parseSort } from '../../lib/list-search.js';

export function PropertyDetailPage() {
  const api = useApi();
  const { data: me } = useMe();
  const { propertyId } = useParams({ strict: false });
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveError, setArchiveError] = useState(/** @type {string|null} */ (null));
  const { data: property, error, isPending, refetch } = useQuery(adminQueries.property(api, propertyId));

  const tab = PROPERTY_TABS.some((t) => t.id === search.tab && !t.disabled && tabAllowed(me, t)) ? search.tab : 'uebersicht';
  const setTab = (next) =>
    navigate({ to: '/liegenschaften/$propertyId', params: { propertyId }, search: { tab: next === 'uebersicht' ? undefined : next }, replace: true });

  const breadcrumb = (
    <Link to="/liegenschaften" className="hover:underline">
      Liegenschaften
    </Link>
  );

  if (error) {
    return (
      <>
        <PageHeader title="Liegenschaft" breadcrumb={breadcrumb} />
        <ErrorState
          error={error}
          onRetry={() => refetch()}
          notFoundTitle="Liegenschaft nicht gefunden"
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/liegenschaften">Zur Liste</Link>
            </Button>
          }
        />
      </>
    );
  }
  if (isPending) return <DetailSkeleton />;

  const canEdit = hasPermission(me, 'properties.update');
  const canArchive = hasPermission(me, 'properties.archive');
  const archived = Boolean(property.archived_at);
  const visibleTabs = PROPERTY_TABS.filter((t) => tabAllowed(me, t));

  /** Archive (only without active units, 409 `property-has-active-units`) or restore. */
  async function toggleArchive() {
    setArchiveError(null);
    try {
      if (archived) await api.admin.properties.restore(property.id);
      else await api.admin.properties.archive(property.id);
      await queryClient.invalidateQueries({ queryKey: keys.admin.resource('properties') });
      queryClient.invalidateQueries({ queryKey: keys.admin.list('property-lookup') });
      queryClient.invalidateQueries({ queryKey: keys.admin.dashboard() });
      toast.success(archived ? 'Liegenschaft wiederhergestellt' : 'Liegenschaft archiviert');
    } catch (e) {
      if (hasProblemType(e, 'property-has-active-units')) {
        setArchiveError(
          'Die Liegenschaft hat noch aktive Einheiten. Archivieren ist erst möglich, wenn alle Einheiten archiviert sind – Einheiten werden aus WinCasa übernommen.',
        );
      } else {
        setArchiveError(getErrorMessage(e, 'Der Vorgang konnte nicht ausgeführt werden.'));
      }
    }
  }

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title={property.name || `Liegenschaft #${property.id}`}
        description={
          <span className="flex flex-wrap items-center gap-2">
            {property.number && <span className="numeric">Nr. {property.number}</span>}
            <span>{property.address?.line}</span>
            {property.is_in_preparation && <Badge variant="warning">Vorbereitungsmodus</Badge>}
            {property.archived_at && <Badge variant="neutral">Archiviert</Badge>}
          </span>
        }
        actions={
          (canEdit || canArchive) && (
            <>
              {canEdit && (
                <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
                  <Pencil aria-hidden="true" /> Bearbeiten
                </Button>
              )}
              {canArchive && (
                <Button size="sm" variant="outline" onClick={() => setArchiveOpen(true)}>
                  {archived ? <ArchiveRestore aria-hidden="true" /> : <Archive aria-hidden="true" />}
                  {archived ? 'Wiederherstellen' : 'Archivieren'}
                </Button>
              )}
            </>
          )
        }
      />
      {archiveError && (
        <Alert variant="destructive" className="mb-3">
          <CircleAlert aria-hidden="true" />
          <AlertTitle>{archived ? 'Wiederherstellen nicht möglich' : 'Archivieren nicht möglich'}</AlertTitle>
          <AlertDescription>{archiveError}</AlertDescription>
        </Alert>
      )}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList aria-label="Bereiche der Liegenschaft">
          {visibleTabs.map((t) => (
            <TabsTrigger key={t.id} value={t.id} disabled={t.disabled}>
              {t.label}
              {t.disabled && (
                <Badge variant="neutral" className="px-1 py-0 text-[10px]">
                  folgt
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="uebersicht">
          <OverviewTab property={property} canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="einheiten">
          <UnitsTab propertyId={propertyId} />
        </TabsContent>
        <TabsContent value="eigentuemer">
          <OwnershipsTab propertyId={propertyId} />
        </TabsContent>
        <TabsContent value="mieter">
          <TenanciesTab propertyId={propertyId} />
        </TabsContent>
        <TabsContent value="finanzen">
          <PropertyFinanceTab propertyId={String(propertyId)} />
        </TabsContent>
        <TabsContent value="dokumente">
          <DocumentManager ownerType="property" ownerId={propertyId} title="Dokumente der Liegenschaft" />
        </TabsContent>
        <TabsContent value="zaehler">
          <PropertyMetersTab propertyId={propertyId} />
        </TabsContent>
        <TabsContent value="belegpruefungen">
          <PropertyDocumentChecksTab propertyId={propertyId} />
        </TabsContent>
        <TabsContent value="kommunikation">
          <PropertyCommunicationTab propertyId={propertyId} />
        </TabsContent>
        <TabsContent value="historie">
          <HistoryPanel subjectType="property" subjectId={propertyId} title="Änderungen an der Liegenschaft" limit={25} />
        </TabsContent>
      </Tabs>
      {canEdit && <PropertyEditSheet property={property} open={editOpen} onOpenChange={setEditOpen} />}
      {canArchive && (
        <ConfirmDialog
          open={archiveOpen}
          onOpenChange={setArchiveOpen}
          title={archived ? 'Liegenschaft wiederherstellen?' : 'Liegenschaft archivieren?'}
          description={
            archived
              ? 'Die Liegenschaft erscheint wieder in Listen, Filtern und Auswertungen.'
              : 'Archivierte Liegenschaften erscheinen nicht mehr in Listen, Filtern und Auswertungen. Nur möglich, wenn keine aktiven Einheiten mehr bestehen.'
          }
          confirmLabel={archived ? 'Wiederherstellen' : 'Archivieren'}
          destructive={!archived}
          onConfirm={toggleArchive}
        />
      )}
    </>
  );
}

function OverviewTab({ property, canEdit }) {
  const api = useApi();
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <div className="grid gap-3 sm:grid-cols-2 lg:col-span-2 xl:grid-cols-4">
        <StatCard label="Einheiten" icon={DoorOpen} value={formatNumber(property.units_count, { maximumFractionDigits: 0 })} />
        <StatCard label="Kundenportal" value={property.portal_enabled ? 'Freigeschaltet' : 'Nicht freigeschaltet'} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle as="h2">Adresse & Stammdaten</CardTitle>
          <CardDescription>Name und Adresse stammen aus WinCasa und sind hier nicht änderbar.</CardDescription>
        </CardHeader>
        <CardContent>
          <DescriptionList
            items={[
              { label: 'Name', value: property.name, hint: 'aus WinCasa' },
              {
                label: 'Adresse',
                value: [
                  [property.address?.street, property.address?.house_number].filter(Boolean).join(' '),
                  [property.address?.postal_code, property.address?.city].filter(Boolean).join(' '),
                  property.address?.country,
                ]
                  .filter(Boolean)
                  .join(', '),
                hint: 'aus WinCasa',
              },
              { label: 'Nummer', value: property.number },
              { label: 'Art', value: property.source_kind_label || property.kind },
              { label: 'Verwalterbestellung bis', value: property.manager_appointment_ends_on ? formatDate(property.manager_appointment_ends_on) : null },
              {
                label: 'Vorbereitungsmodus',
                value: property.is_in_preparation ? <Badge variant="warning">Aktiv</Badge> : <Badge variant="neutral">Aus</Badge>,
              },
              { label: 'Koordinaten', value: <Coordinates property={property} canEdit={canEdit} /> },
            ]}
          />
        </CardContent>
      </Card>
      {property.has_image && (
        <Card>
          <CardHeader>
            <CardTitle as="h2">Bild</CardTitle>
            <CardDescription>Wird im Kundenportal bei der Liegenschaft angezeigt.</CardDescription>
          </CardHeader>
          <CardContent>
            <img
              src={api.admin.properties.imageUrl(property.id, property.image_version)}
              alt={`Bild ${property.name ?? ''}`.trim()}
              className="max-h-64 w-full rounded-md border object-cover"
              loading="lazy"
            />
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader>
          <CardTitle as="h2">Zuständigkeiten</CardTitle>
        </CardHeader>
        <CardContent>
          <DescriptionList
            items={[
              { label: 'Verwalter', value: property.manager ? <PartyLink party={property.manager} /> : null },
              { label: 'Ansprechpartner', value: property.contact ? <PartyLink party={property.contact} /> : null },
            ]}
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle as="h2">Beschreibung</CardTitle>
        </CardHeader>
        <CardContent>
          {property.description ? (
            <p className="whitespace-pre-line text-sm">{property.description}</p>
          ) : (
            <p className="text-sm text-muted-foreground">Keine Beschreibung hinterlegt.</p>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle as="h2">Externe Schlüssel</CardTitle>
          <CardDescription>Zuordnung zu Cobra, WinCasa und Altsystemen</CardDescription>
        </CardHeader>
        <CardContent>
          <ExternalReferences references={property.external_references} />
        </CardContent>
      </Card>
      <PropertyRolesCard property={property} canEdit={canEdit} />
    </div>
  );
}

/** "01.01.2020 – heute" @param {string|null|undefined} from @param {string|null|undefined} to */
function period(from, to) {
  if (!from && !to) return '';
  return `${from ? formatDate(from) : 'unbekannt'} – ${to ? formatDate(to) : 'heute'}`;
}

function UnitsTab({ propertyId }) {
  const api = useApi();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [sorting, setSorting] = useState(() => parseSort('number'));
  const params = {
    page: pagination.pageIndex + 1,
    per_page: pagination.pageSize,
    sort: sorting.map((s) => (s.desc ? `-${s.id}` : s.id)).join(',') || undefined,
    filter: { property_id: propertyId },
  };
  const { data, error, isPending, isFetching, refetch } = useQuery(adminQueries.units(api, params));
  const columns = useMemo(
    () => [
      { accessorKey: 'number', header: 'Nr.', cell: ({ row }) => <UnitLabel unit={{ id: row.original.id, number: row.original.number }} /> },
      { accessorKey: 'designation', header: 'Bezeichnung', cell: ({ row }) => row.original.designation ?? '–' },
      { id: 'type', header: 'Typ', enableSorting: false, cell: ({ row }) => row.original.type?.label ?? row.original.source_type_label ?? '–' },
      { accessorKey: 'living_space', header: 'Wohnfläche', meta: { numeric: true }, cell: ({ row }) => formatArea(row.original.living_space) },
    ],
    [],
  );
  if (error) return <ErrorState error={error} onRetry={() => refetch()} />;
  return (
    <DataTable
      label="Einheiten der Liegenschaft"
      columns={columns}
      data={data?.data}
      rowCount={data?.meta?.total}
      isLoading={isPending}
      isFetching={isFetching}
      getRowId={(row) => String(row.id)}
      pagination={pagination}
      onPaginationChange={setPagination}
      sorting={sorting}
      onSortingChange={(next) => {
        setSorting(next);
        setPagination((p) => ({ ...p, pageIndex: 0 }));
      }}
      emptyTitle="Keine Einheiten"
    />
  );
}

const OWNERSHIP_VIEWS = [
  { value: 'current', label: 'Aktuell' },
  { value: 'all', label: 'Alle (inkl. Historie)' },
];

function OwnershipsTab({ propertyId }) {
  const api = useApi();
  const [view, setView] = useState('current');
  const { data, error, isPending, refetch } = useQuery(adminQueries.propertyOwnerships(api, propertyId, { current: view === 'current' }));
  const columns = useMemo(
    () => [
      { id: 'unit', header: 'Einheit', cell: ({ row }) => <UnitLabel unit={row.original.unit} /> },
      { id: 'owners', header: 'Eigentümer', cell: ({ row }) => <PartyList parties={row.original.parties} roleLabels={OWNERSHIP_PARTY_ROLE_LABELS} /> },
      { id: 'account', header: 'Konto', cell: ({ row }) => <span className="numeric">{row.original.account_number ?? '–'}</span> },
      { id: 'period', header: 'Zeitraum', cell: ({ row }) => <span className="numeric">{period(row.original.valid_from, row.original.valid_to) || '–'}</span> },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (row.original.is_current ? <Badge variant="success">Aktuell</Badge> : <Badge variant="neutral">Historisch</Badge>),
      },
    ],
    [],
  );
  return (
    <div className="flex flex-col gap-3">
      <ToggleGroup label="Eigentumsverhältnisse anzeigen" value={view} onValueChange={setView} options={OWNERSHIP_VIEWS} />
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Eigentumsverhältnisse"
          columns={columns}
          data={data}
          isLoading={isPending}
          getRowId={(row) => String(row.id)}
          emptyState={
            <EmptyState
              compact
              className="border-0"
              icon={Users}
              title={view === 'current' ? 'Keine aktuellen Eigentumsverhältnisse' : 'Keine Eigentumsverhältnisse'}
            />
          }
        />
      )}
    </div>
  );
}

const TENANCY_VIEWS = [
  { value: 'current', label: 'Aktuell' },
  { value: 'past', label: 'Ehemalig' },
];

function TenanciesTab({ propertyId }) {
  const api = useApi();
  const [status, setStatus] = useState('current');
  const { data, error, isPending, refetch } = useQuery(adminQueries.propertyTenancies(api, propertyId, { status }));
  const columns = useMemo(
    () => [
      { id: 'unit', header: 'Einheit', cell: ({ row }) => <UnitLabel unit={row.original.unit} /> },
      { id: 'tenants', header: 'Mieter', cell: ({ row }) => <PartyList parties={row.original.parties} roleLabels={TENANCY_PARTY_ROLE_LABELS} /> },
      { id: 'account', header: 'Konto', cell: ({ row }) => <span className="numeric">{row.original.account_number ?? '–'}</span> },
      { id: 'starts_on', header: 'Beginn', cell: ({ row }) => <span className="numeric">{formatDate(row.original.starts_on)}</span> },
      { id: 'ends_on', header: 'Ende', cell: ({ row }) => <span className="numeric">{formatDate(row.original.ends_on)}</span> },
      { id: 'terminated_on', header: 'Gekündigt am', cell: ({ row }) => <span className="numeric">{formatDate(row.original.terminated_on)}</span> },
      { id: 'status', header: 'Status', cell: ({ row }) => <StatusBadge domain="tenancy" status={row.original.status} /> },
    ],
    [],
  );
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ToggleGroup label="Mietverhältnisse anzeigen" value={status} onValueChange={setStatus} options={TENANCY_VIEWS} />
        <p className="text-xs text-muted-foreground">Quelle: Buchhaltung (nur lesend)</p>
      </div>
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Mietverhältnisse"
          columns={columns}
          data={data}
          isLoading={isPending}
          getRowId={(row) => String(row.id)}
          emptyState={
            <EmptyState
              compact
              className="border-0"
              icon={KeyRound}
              title={status === 'current' ? 'Keine aktuellen Mietverhältnisse' : 'Keine ehemaligen Mietverhältnisse'}
            />
          }
        />
      )}
    </div>
  );
}

/**
 * Koordinaten (F-115) mit OpenStreetMap-Link (keine eingebetteten Kacheln Dritter) und Aktion „Koordinaten ermitteln“,
 * sofern ein Geokodierungsdienst konfiguriert ist (die API meldet `geocoding_available`).
 */
function Coordinates({ property, canEdit }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const has = property.latitude != null && property.longitude != null;
  const geocode = useMutation({
    mutationFn: () => api.admin.properties.geocode(property.id),
    onSuccess: (updated) => {
      queryClient.setQueryData(keys.admin.detail('properties', property.id), updated);
      toast.success('Koordinaten ermittelt');
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Die Koordinaten konnten nicht ermittelt werden.')),
  });
  const run = () => (has ? setConfirmOpen(true) : geocode.mutate());

  return (
    <span className="flex flex-col items-start gap-1.5">
      {has ? (
        <span className="flex flex-wrap items-center gap-2">
          <span className="numeric">
            {formatNumber(property.latitude, { maximumFractionDigits: 6 })}, {formatNumber(property.longitude, { maximumFractionDigits: 6 })}
          </span>
          {property.coordinates_source && <Badge variant="neutral">{labelOf(COORDINATES_SOURCE_LABELS, property.coordinates_source)}</Badge>}
          {property.map_url && (
            <a href={property.map_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
              <MapPin className="size-3.5" aria-hidden="true" /> Auf Karte zeigen<span className="sr-only"> (OpenStreetMap, neues Fenster)</span>
            </a>
          )}
        </span>
      ) : (
        <span className="text-muted-foreground">nicht hinterlegt</span>
      )}
      {canEdit && property.geocoding_available && (
        <Button size="sm" variant="outline" loading={geocode.isPending} onClick={run}>
          <LocateFixed aria-hidden="true" /> Koordinaten ermitteln
        </Button>
      )}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Koordinaten neu ermitteln?"
        description="Die vorhandenen Koordinaten werden durch das Ergebnis des Geokodierungsdienstes ersetzt. Übermittelt wird nur die Adresse der Liegenschaft."
        confirmLabel="Ermitteln"
        onConfirm={() => geocode.mutateAsync()}
      />
    </span>
  );
}
